import React, { useContext, useLayoutEffect, useRef } from "react";
import { SidebarContext } from "./Sidebar";
import Button from "./Button";
import styled from "styled-components";
import colors from "../common/colors";
import ObstructionCard from "./Obstruction/Card";
import Loading from "./Loading";
import List from "./List";
import { setListExpanded, getListIsExpanded } from "./List/redux";
import { useDispatch, useSelector } from "react-redux";

const Wrapper = styled.div`
  padding: 2rem 1rem;
  border-bottom: 1px solid ${colors.gray200};
  background: ${colors.white};
`;

const H2 = styled.h2`
  font-weight: normal;
  margin-bottom: 1rem;
  color: ${colors.logoblauw};
`;

// Color followed links as unfollowed links
const TextBelowTitle = styled.p`
  color: #154273;
  a, a:visited {
    color: #154273;
    text-decoration: underline;
  }
  margin-bottom: 28px;
`;

function ObstructionList(props) {
  const { items, count, loaded, error = {}, limited, pageContext } = props;

  const { detectScrollTop, setScrollTop } = useContext(SidebarContext);

  const expanded = useSelector(getListIsExpanded);
  const dispatch = useDispatch();

  const sixthListItem = useRef();

  const onExpandClick = () => {
    dispatch(setListExpanded(!expanded));
    setScrollTop(expanded ? 1 : 0);
  };

  // when expanding the list, focus on the first revealed item.
  useLayoutEffect(() => {
    if (expanded && sixthListItem?.current) {
      sixthListItem.current.focus();
    }
  }, [expanded]);

  const renderEmptyText = () => {
    switch (pageContext) {
      case "traffic":
        return (
          <>
            <H2>Geen files gevonden</H2>
            <TextBelowTitle>
              Er zijn op dit moment geen files.
              We tonen alleen informatie over de <a target="_blank" href="https://www.rijkswaterstaat.nl/wegen/wegenoverzicht">rijkswegen</a>.
              Dat zijn veelal snelwegen. Provinciale en gemeentelijke wegen tonen we niet.<br />
              Je ziet alleen verkeersinformatie op basis van de filters die aanstaan. Je kunt deze filters aanpassen.
            </TextBelowTitle>
          </>
        );
      case "roadworks":
        return (
          <>
            <H2>Geen wegwerkzaamheden gevonden</H2>
            <TextBelowTitle>
              Er zijn op dit moment geen wegwerkzaamheden.
              We tonen alleen informatie over de <a target="_blank" href="https://www.rijkswaterstaat.nl/wegen/wegenoverzicht">rijkswegen</a>.
              Dat zijn veelal snelwegen. Provinciale en gemeentelijke wegen tonen we niet.
            </TextBelowTitle>
          </>
        );
      case "search":
        return (
          <>
            <H2>Geen actuele meldingen gevonden</H2>
            <TextBelowTitle>
              Er zijn geen files op jouw route. Ook zijn er geen werkzaamheden waarbij we meer dan 10 minuten vertraging verwachten.
              We tonen alleen informatie over de <a target="_blank" href="https://www.rijkswaterstaat.nl/wegen/wegenoverzicht">rijkswegen</a>.
              Dat zijn veelal snelwegen. Provinciale en gemeentelijke wegen tonen we niet.<br />
              <br/>
              Wil je werkzaamheden zien waarbij we minder dan 10 minuten vertraging verwachten? Bekijk dan de <a href="/wegwerkzaamheden">werkzaamhedenpagina</a>.
            </TextBelowTitle>
          </>
        );
      case "home":
      default:
        return (
          <>
            <H2>Geen actuele meldingen gevonden</H2>
            <TextBelowTitle>
              Er zijn op dit moment geen files en volledige wegafsluitingen.
              We tonen alleen informatie over de <a target="_blank" href="https://www.rijkswaterstaat.nl/wegen/wegenoverzicht">rijkswegen</a>.
              Dat zijn veelal snelwegen. Provinciale en gemeentelijke wegen tonen we niet.<br />
              Je ziet alleen verkeersinformatie op basis van de filters die aanstaan. Je kunt deze filters aanpassen.
            </TextBelowTitle>
          </>
        );
    }
  };

  return (
    <Wrapper style={props.style}>
      {loaded ? (
        <>
          <List>
            {items.map((obstruction, index) => (
              <ObstructionCard
                key={obstruction.obstructionId}
                {...obstruction}
                onClick={detectScrollTop}
                ref={index === 5 ? sixthListItem : undefined}
              />
            ))}
            {items.length === 0 && Object.keys(error).length === 0 && renderEmptyText()}
          </List>
          {count > 5 && limited && (
            <Button outline block onClick={onExpandClick}>
              {expanded ? "Lijst inklappen" : <>Alle resultaten ({count})</>}
            </Button>
          )}
        </>
      ) : Object.keys(error).length ? (
        <p>Data kon niet geladen worden.</p>
      ) : (
        <Loading text="Bezig met laden..." />
      )}
    </Wrapper>
  );
}

ObstructionList.defaultProps = {
  limited: true,
  count: 0,
  pageContext: "home",
};

export default ObstructionList;
